import { DirectionalHint, TooltipDelay } from "@fluentui/react";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import GetToolSVG from "./GetToolSVG";
import ViewportContext from "../../../../../context/studyviewer/ViewportContext";
import {
  resetMPRViewports,
  exitMPRViewports,
  MIRRORABLE_3D_TOOL_NAMES,
  setMPRCrosshairsHidden,
} from "../../../helpers/CornerstoneViewportService";
import { VOLUME_3D_VIEWPORT_INDEX } from "../../../helpers/MPRViewportInput";
import { isMobile } from "react-device-detect";
import { purgeVolumeCache } from "../../../helpers/CornerstoneService";
import {
  PanTool,
  StackScrollTool,
  WindowLevelTool,
  ZoomTool,
  CrosshairsTool,
  PlanarRotateTool,
} from "@cornerstonejs/tools";
import WindowLevelToolComponent from "./WindowLevelToolComponent";
// import { logAmplitudeEventWithUsernameAndRole } from "../../../../../helpers/Amplitude";
import userContext from "../../../../../context/users/UserContext";
import CustomToolTipHost from "../../../../../components/CustomToolTipHost/CustomToolTipHost";
import ToolbarMoreMenuRow from "../../Toolbar/ToolbarMoreMenuRow";
import { MOUSE_ASSIGNABLE_TOOL_NAMES } from "../../../../../constants/MouseButtonToolAssignments";
import {
  createMouseButtonAssignmentPointerDownCaptureHandler,
  suppressMouseButtonAssignmentContextMenu,
  ToolbarMouseAssignmentIndicators,
} from "./mouseButtonToolbarUtils";

const toolStyle = {
  margin: `${isMobile ? "0px" : "0 5px"}`,
  height: "100%",
  width: "fit-content",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const toolTipStyles = {
  root: {
    height: "100%",
  },
};

const MPR_MOUSE_ASSIGNABLE_ICON_TOOLS = [
  StackScrollTool.toolName,
  ZoomTool.toolName,
  PanTool.toolName,
];

// Tools that stay usable — and therefore stay enabled — once the 3D viewport
// is the active viewport: the camera/VOI-based tools that actually work on
// VOLUME_3D (see MIRRORABLE_3D_TOOL_NAMES), the 3D rotate tool itself, and
// Reset3D, which resets every MPR viewport including the 3D one.
const TOOLS_ENABLED_FOR_3D = new Set([
  ...MIRRORABLE_3D_TOOL_NAMES,
  PlanarRotateTool.toolName,
  "Reset3D",
]);

const MPRTool = ({ tool, variant = "toolbar", onSelect }) => {
  const {
    setIsMPRActive,
    activeMPRTool,
    setActiveMPRTool,
    setPreviousActiveMPRTool,
    isMPRCineEnabled,
    setIsMPRCineEnabled,
    isMPREnabled,
    handleDefaultToolChange,
    assignMouseButtonToAssignableTool,
    mouseButtonToolAssignments,
    is3DActive,
    setIs3DActive,
    activeMPRViewportIndex,
  } = useContext(ViewportContext);
  const { username } = useContext(userContext);
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const handleMouseOver = useCallback(() => {
    if (!hovered) setHovered(true);
  }, [hovered]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);

  const changeTool = useCallback(async (tool) => {
    onSelect?.();
    if (tool.key === "EXITMPR") {
      await exitMPRViewports(is3DActive, setIs3DActive, setIsMPRActive);
    } else if (tool.key === "CINE") {
      if (tool.isEvent && !isMPRCineEnabled) {
        // logAmplitudeEventWithUsernameAndRole(tool.eventName, {}, username);
      }
      setIsMPRCineEnabled(!isMPRCineEnabled);
    } else if (tool.key === "Reset3D") {
      resetMPRViewports();
      if (tool.isEvent) {
        // logAmplitudeEventWithUsernameAndRole(tool.eventName, {}, username);
      }
    } else if (tool.key === CrosshairsTool.toolName) {
      // Crosshairs is a visibility toggle, not just a tool selection.
      // Deselecting it only demoted the tool to Passive so its reference lines
      // stayed drawn (deliberate — they remain draggable next to a measurement
      // tool), which left no way to switch off the crosshairs MPR turns on by
      // default. Clicking the button while Crosshairs is active now hides them
      // outright; clicking it again brings them back on the Primary button.
      setPreviousActiveMPRTool(activeMPRTool);
      if (activeMPRTool === tool.key) {
        setMPRCrosshairsHidden(true);
        handleDefaultToolChange();
      } else {
        setMPRCrosshairsHidden(false);
        setActiveMPRTool(tool.key);
        if (tool.isEvent) {
          // logAmplitudeEventWithUsernameAndRole(tool.eventName, {}, username);
        }
      }
    } else {
      if (activeMPRTool === tool.key) {
        setPreviousActiveMPRTool(activeMPRTool);
        handleDefaultToolChange();
      } else {
        setPreviousActiveMPRTool(activeMPRTool);
        if (MOUSE_ASSIGNABLE_TOOL_NAMES.includes(tool.key)) {
          assignMouseButtonToAssignableTool("primary", tool.key, true);
        }
        setActiveMPRTool(tool.key);
        if (tool.isEvent) {
          // logAmplitudeEventWithUsernameAndRole(tool.eventName, {}, username);
        }
      }
    }
  }, [is3DActive, setIs3DActive, setIsMPRActive, isMPRCineEnabled, setIsMPRCineEnabled,
      activeMPRTool, setPreviousActiveMPRTool, handleDefaultToolChange, setActiveMPRTool,
      assignMouseButtonToAssignableTool, username, onSelect]);

  // The 3D volume-render viewport can't run crosshairs, stack-scroll,
  // reference-lines, CINE, or any annotation/measurement tool — only
  // Zoom/Pan/WindowLevel and the rotate tool itself work there. When it's the
  // active viewport, grey out and disable everything outside that set.
  const isUnusableWith3DActive =
    activeMPRViewportIndex === VOLUME_3D_VIEWPORT_INDEX &&
    !TOOLS_ENABLED_FOR_3D.has(tool.key);
  const enabled = isMPREnabled && !isUnusableWith3DActive;

  const dynamicToolStyle = useMemo(() => ({
    ...toolStyle,
    justifyContent: isMobile ? "flex-start" : "center",
    opacity: enabled ? 1 : 0.5,
    pointerEvents: enabled ? "auto" : "none",
  }), [enabled]);

  if (variant === "menu") {
    return (
      <ToolbarMoreMenuRow
        label={t(tool.name)}
        onClick={enabled ? () => changeTool(tool) : undefined}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        disabled={!enabled}
      >
        <div className="highlight-tool">
          <GetToolSVG toolName={tool.key} hovered={hovered} />
        </div>
      </ToolbarMoreMenuRow>
    );
  }

  const renderDesktopToolIcon = () => {
    if (tool.key === WindowLevelTool.toolName) {
      return (
        <WindowLevelToolComponent toolName={tool.key} hovered={hovered} tool={tool} />
      );
    }

    if (MPR_MOUSE_ASSIGNABLE_ICON_TOOLS.includes(tool.key)) {
      return (
        <div className="highlight-tool">
          <div
            className={!isMobile ? "tool-with-mouse" : ""}
            style={{
              height: "100%",
              position: "relative",
            }}
            onPointerDownCapture={createMouseButtonAssignmentPointerDownCaptureHandler(
              tool.key,
              assignMouseButtonToAssignableTool
            )}
            onContextMenu={suppressMouseButtonAssignmentContextMenu}
            onContextMenuCapture={suppressMouseButtonAssignmentContextMenu}
          >
            <GetToolSVG toolName={tool.key} hovered={hovered} />
            {!isMobile && (
              <ToolbarMouseAssignmentIndicators
                toolName={tool.key}
                assignments={mouseButtonToolAssignments}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="highlight-tool">
        <GetToolSVG toolName={tool.key} hovered={hovered} />
      </div>
    );
  };

  return (
    <CustomToolTipHost
      delay={TooltipDelay.zero}
      id={tool.key}
      directionalHint={DirectionalHint.bottom}
      styles={toolTipStyles}
      content={isMobile ? "" : t(tool.name)}
    >
      <div
        onClick={enabled ? () => changeTool(tool) : undefined}
        onMouseOver={enabled ? handleMouseOver : undefined}
        onMouseLeave={enabled ? handleMouseLeave : undefined}
        style={dynamicToolStyle}
        aria-describedby={tool.key}
      >
        {isMobile ? (
          <>
            <div
              style={{
                height: "65%",
                width: "60px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GetToolSVG toolName={tool.key} hovered={hovered} />
            </div>
            <span
              style={{
                fontSize: "8px",
                lineHeight: "100%",
                fontWeight: "400",
                color: "#FFFFFF",
              }}
            >
              {t(tool.name)}
            </span>
          </>
        ) : (
          renderDesktopToolIcon()
        )}
      </div>
    </CustomToolTipHost>
  );
};

export default MPRTool;
